import { useEffect, useState } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useProjects } from "../../useHook";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendPrefixedImageSeoToFormData,
} from "../../../../Components/Shared/ImageSeoFields";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";

const emptyFormData = {
  categoryId: "",
  title: "",
  title_ur: "",
  customUrl: "",
  description: "",
  description_ur: "",
  headerImage: null,
  header_image_alt: "",
  header_image_title: "",
};

export default function OurProjectForm({ isEditMode = false }) {
  const navigate = useNavigate();
  const { id: categoryId, projectId } = useParams();
  const { addProject, updateProject, getProjectByIdData } = useProjects();
  const listPath = `/our-projects/${categoryId}`;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [formData, setFormData] = useState({
    ...emptyFormData,
    categoryId: categoryId || "",
  });
  const [projectImages, setProjectImages] = useState([]);
  const [existingProjectImages, setExistingProjectImages] = useState([]);
  const [newImagePositions, setNewImagePositions] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [primaryExistingImageId, setPrimaryExistingImageId] = useState(null);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  useEffect(() => {
    if (!isEditMode || !projectId) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoadingProject(true);
      try {
        const project = await getProjectByIdData(projectId, () => {});
        if (!cancelled && project) loadProjectForEdit(project);
      } catch (error) {
        console.error("Error loading project for edit:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to load project data for editing.",
          icon: "error",
        });
      } finally {
        if (!cancelled) setIsLoadingProject(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, projectId]);

  const loadProjectForEdit = (projectData) => {
    setFormData({
      categoryId: projectData.categoryId || projectData.category?.id || categoryId || "",
      title: projectData.title || projectData.name || "",
      title_ur: projectData.title_ur || projectData.name_ur || "",
      description: projectData.description || "",
      description_ur: projectData.description_ur || "",
      customUrl: projectData.customUrl || "",
      headerImage: projectData.headerImage
        ? {
            preview: projectData.headerImage,
            file: null,
          }
        : null,
      header_image_alt: projectData.header_image_alt || "",
      header_image_title: projectData.header_image_title || "",
    });

    if (projectData.projectImages && Array.isArray(projectData.projectImages)) {
      setExistingProjectImages(projectData.projectImages);
      setSelectedImageIds(projectData.projectImages.map((img) => img.id));
      const primaryImg = projectData.projectImages.find((img) => img.is_primary);
      if (primaryImg) {
        setPrimaryExistingImageId(primaryImg.id);
        setPrimaryImageIndex(null);
      } else {
        setPrimaryExistingImageId(null);
        setPrimaryImageIndex(0);
      }
    } else {
      setExistingProjectImages([]);
      setSelectedImageIds([]);
      setPrimaryExistingImageId(null);
      setPrimaryImageIndex(0);
    }
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const checkImageResolution = (imageFile) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve(img.width === 450 && img.height === 450);
        };
        img.src = URL.createObjectURL(imageFile);
      });

    const isValid = await checkImageResolution(file);
    if (isValid) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          headerImage: {
            preview: reader.result,
            file,
          },
        }));
      };
      reader.readAsDataURL(file);
    } else {
      Swal.fire({
        title: "Invalid Image Resolution",
        text: `Image "${file.name}" does not have the required resolution of 450x450. Please upload the correct resolution.`,
        icon: "error",
      });
    }
  };

  const handleProjectImagesUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    const input = event.target;
    if (!files.length) return;

    const checkImageResolution = (file) =>
      new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img.width === 1200 && img.height === 900);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(false);
        };
        img.src = objectUrl;
      });

    const results = await Promise.all(files.map((file) => checkImageResolution(file)));
    const validFiles = [];
    const rejectedNames = [];
    results.forEach((isValid, index) => {
      if (isValid) validFiles.push(files[index]);
      else rejectedNames.push(files[index].name);
    });
    input.value = "";

    if (rejectedNames.length) {
      Swal.fire({
        title: "Invalid Image Resolution",
        html: `These gallery images must be exactly <b>1200 × 900</b> pixels and were not added:<br/>${rejectedNames
          .map((name) => `• ${name}`)
          .join("<br/>")}`,
        icon: "error",
      });
    }
    if (!validFiles.length) return;

    const imageObjects = await Promise.all(
      validFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                preview: reader.result,
                file,
                name: file.name,
                imageAlt: "",
                imageTitle: "",
              });
            };
            reader.readAsDataURL(file);
          }),
      ),
    );
    setProjectImages((prev) => [...prev, ...imageObjects]);
  };

  const removeProjectImage = (index) => {
    setProjectImages((prev) => prev.filter((_, i) => i !== index));
    setNewImagePositions((prev) => prev.filter((_, i) => i !== index));
    if (primaryImageIndex === index) {
      setPrimaryImageIndex(0);
    } else if (primaryImageIndex > index) {
      setPrimaryImageIndex((prev) => prev - 1);
    }
  };

  const handleSetPrimaryNew = (index) => {
    setPrimaryImageIndex(index);
    setPrimaryExistingImageId(null);
  };

  const handleSetPrimaryExisting = (imageId) => {
    setPrimaryExistingImageId(imageId);
    setPrimaryImageIndex(null);
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    setExistingProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onExistingImageSeoChange = (index, field, value) => {
    setExistingProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewProjectImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlt" : "imageTitle";
    setProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [key]: value } : img)),
    );
  };

  const onNewImagePositionChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    setNewImagePositions((prev) => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  };

  const handleImageToggle = (imageId) => {
    setSelectedImageIds((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId],
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const goBack = () => navigate(listPath);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.headerImage) {
      Swal.fire({
        title: "Error",
        text: "Header image is required. Please upload a header image.",
        icon: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();

      formDataObj.append("title", formData.title);
      if (formData.title_ur) formDataObj.append("title_ur", formData.title_ur);
      formDataObj.append("customUrl", formData.customUrl);
      formDataObj.append("description", formData.description || "");
      if (formData.description_ur)
        formDataObj.append("description_ur", formData.description_ur);
      formDataObj.append("categoryId", categoryId);

      if (formData.headerImage?.file) {
        formDataObj.append("headerImage", formData.headerImage.file);
      }
      appendPrefixedImageSeoToFormData(
        formDataObj,
        "header_image",
        formData.header_image_alt,
        formData.header_image_title,
      );

      projectImages.forEach((image, index) => {
        if (image.file) {
          formDataObj.append("projectImages", image.file);
          formDataObj.append(
            "imagePosition",
            newImagePositions[index] != null ? newImagePositions[index] : index + 1,
          );
          formDataObj.append(
            "imageAlt",
            String(image.imageAlt ?? "").trim(),
          );
          formDataObj.append(
            "imageTitle",
            String(image.imageTitle ?? "").trim(),
          );
        }
      });

      if (isEditMode) {
        formDataObj.append("keepImageIds", JSON.stringify(selectedImageIds));
        const positions = {};
        const existingImageAlts = {};
        const existingImageTitles = {};
        existingProjectImages.forEach((img) => {
          if (img.position !== undefined && img.position !== null) {
            positions[img.id] = img.position;
          }
          if (img.id == null) return;
          existingImageAlts[img.id] = String(img.image_alt ?? "").trim();
          existingImageTitles[img.id] = String(img.image_title ?? "").trim();
        });
        formDataObj.append("existingImagePositions", JSON.stringify(positions));
        formDataObj.append(
          "existingImageAlts",
          JSON.stringify(existingImageAlts),
        );
        formDataObj.append(
          "existingImageTitles",
          JSON.stringify(existingImageTitles),
        );
        if (primaryExistingImageId !== null) {
          formDataObj.append("primaryExistingImageId", primaryExistingImageId);
        }
      }

      if (primaryImageIndex !== null) {
        formDataObj.append("primaryImageIndex", primaryImageIndex);
      }

      if (isEditMode) {
        await updateProject(projectId, formDataObj);
      } else {
        await addProject(formDataObj);
      }

      Swal.fire({
        title: "Success!",
        text: isEditMode
          ? "Project has been updated successfully."
          : "Project has been created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      goBack();
    } catch (error) {
      console.error("Error saving project:", error);
      Swal.fire({
        title: "Error!",
        text: isEditMode
          ? "Failed to update project. Please try again."
          : "Failed to create project. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const hasRequiredFields = formData.title.trim() !== "" && Boolean(categoryId);
    const hasHeaderImage = formData.headerImage !== null;
    return hasRequiredFields && hasHeaderImage;
  };

  if (isLoadingProject) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-b-4 border-[#982598]" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" className="ui-btn-back" onClick={goBack} disabled={isSubmitting}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-xl font-extrabold text-[#15173d]">
              {isEditMode ? "Edit Project" : "Add Project"}
            </h2>
            <p className="text-xs text-gray-500">House construction project</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="ui-btn-cancel" onClick={goBack} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            className="ui-btn-save"
            disabled={isSubmitting || !isFormValid()}
          >
            <Save size={15} />
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">Header Image *</label>
        <div
          className={`mt-1 flex justify-center rounded-xl border-2 border-dashed px-6 pb-6 pt-5 transition-colors ${
            !formData.headerImage
              ? "border-red-300 bg-red-50 hover:border-red-400"
              : "border-gray-300 hover:border-[#982598]"
          }`}
        >
          <div className="space-y-1 text-center">
            {formData.headerImage ? (
              <div className="group relative">
                <img
                  src={
                    formData.headerImage.preview
                      ? formData.headerImage.preview
                      : formData.headerImage
                  }
                  alt="Header preview"
                  className="mx-auto h-48 w-full rounded-lg object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black bg-opacity-0 opacity-0 transition-all duration-300 group-hover:bg-opacity-30 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        headerImage: null,
                      }))
                    }
                    className="rounded-full bg-red-500 p-2 text-white hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="header-image"
                    className="relative cursor-pointer rounded-md bg-white font-medium text-[#982598]"
                  >
                    <span>Upload a header image</span>
                    <input
                      id="header-image"
                      name="header-image"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleHeaderImageUpload}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF · 450x450 pixels *
                </p>
              </>
            )}
          </div>
        </div>
        <ImageSeoFields
          imageAlt={formData.header_image_alt}
          imageTitle={formData.header_image_title}
          onAltChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              header_image_alt: value,
            }))
          }
          onTitleChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              header_image_title: value,
            }))
          }
          className="mt-4"
        />
      </div>

      <div className="ui-card grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="ui-form-label">Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="ui-input-field"
            required
          />
          <UrduInput
            label="عنوان"
            name="title_ur"
            value={formData.title_ur}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <label className="ui-form-label">Custom URL</label>
          <input
            type="text"
            name="customUrl"
            value={formData.customUrl}
            onChange={handleInputChange}
            className="ui-input-field"
            placeholder="Enter custom URL (e.g., my-project-url)"
          />
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">Description</label>
        <RichTextEditor
          value={formData.description}
          onChange={(html) =>
            setFormData((prev) => ({ ...prev, description: html }))
          }
          placeholder="Write project description..."
          minHeight={180}
          maxHeight={420}
        />
        <div className="mt-4">
          <label className="ui-form-label text-right" dir="rtl">
            تفصیل
          </label>
          <RichTextEditor
            dir="rtl"
            value={formData.description_ur}
            onChange={(html) =>
              setFormData((prev) => ({ ...prev, description_ur: html }))
            }
            placeholder="اردو میں تفصیل لکھیں..."
            minHeight={180}
            maxHeight={420}
          />
        </div>
      </div>

      {isEditMode && existingProjectImages.length > 0 && (
        <div className="ui-card">
          <label className="ui-form-label">
            Existing Project Images
          </label>
          <div className="mt-2 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {existingProjectImages.map((image, index) => (
              <div key={image.id} className="group relative">
                <div
                  className={`relative flex h-24 items-center justify-center overflow-hidden rounded-md border bg-gray-100 transition-all ${
                    primaryExistingImageId === image.id ? "ring-2 ring-blue-500" : ""
                  } ${!selectedImageIds.includes(image.id) ? "opacity-40" : ""}`}
                >
                  <img
                    src={image.image_url}
                    alt={`Project ${image.id}`}
                    className="h-full w-full object-cover"
                  />
                  {primaryExistingImageId === image.id &&
                    selectedImageIds.includes(image.id) && (
                      <span className="absolute left-1 top-1 z-20 rounded bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        Primary
                      </span>
                    )}
                  {selectedImageIds.includes(image.id) &&
                    primaryExistingImageId !== image.id && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetPrimaryExisting(image.id);
                        }}
                        className="absolute bottom-1 left-1 z-20 rounded-full bg-white p-1 text-gray-600 opacity-0 shadow-md group-hover:opacity-100"
                        title="Set as Primary Image"
                      >
                        ★
                      </button>
                    )}
                  {!selectedImageIds.includes(image.id) && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-500 bg-opacity-50">
                      <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white">
                        Will be deleted
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleImageToggle(image.id)}
                  className={`absolute -right-2 -top-2 z-20 rounded-full p-1 text-white shadow-md ${
                    !selectedImageIds.includes(image.id)
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-red-500 opacity-0 hover:bg-red-600 group-hover:opacity-100"
                  }`}
                  title={
                    selectedImageIds.includes(image.id)
                      ? "Delete image"
                      : "Restore image"
                  }
                >
                  {selectedImageIds.includes(image.id) ? "×" : "↺"}
                </button>
                {selectedImageIds.includes(image.id) &&
                primaryExistingImageId !== image.id ? (
                  <div className="mt-1">
                    <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                      Position
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[1-9][0-9]*"
                      value={image.position || ""}
                      onChange={(e) =>
                        onExistingImagePositionChange(index, e.target.value)
                      }
                      className="ui-input-field w-full px-1 py-1 text-xs"
                      placeholder="1"
                    />
                  </div>
                ) : selectedImageIds.includes(image.id) &&
                  primaryExistingImageId === image.id ? (
                  <p className="mt-1 text-center text-[10px] font-medium text-blue-600">
                    Primary (not indexed)
                  </p>
                ) : null}
                {selectedImageIds.includes(image.id) && (
                  <ImageSeoFields
                    stacked
                    imageAlt={image.image_alt || ""}
                    imageTitle={image.image_title || ""}
                    onAltChange={(value) =>
                      onExistingImageSeoChange(index, "image_alt", value)
                    }
                    onTitleChange={(value) =>
                      onExistingImageSeoChange(index, "image_title", value)
                    }
                    className="mt-2"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ui-card">
        <label className="ui-form-label">Project Images</label>
        <label
          htmlFor="project-images"
          className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 px-6 py-8 transition-all hover:border-[#982598] hover:bg-[#982598]/5"
        >
          <p className="mb-1 text-sm font-medium text-gray-700">Click to select images</p>
          <p className="text-xs text-gray-500">
            PNG, JPG, GIF · Multiple images · Exactly 1200 × 900
          </p>
          <input
            id="project-images"
            name="project-images"
            type="file"
            className="sr-only"
            accept="image/*"
            multiple
            onChange={handleProjectImagesUpload}
          />
        </label>

        {projectImages.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm text-gray-600">
              {projectImages.length} new image(s) selected
            </p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {projectImages.map((image, index) => (
                <div key={index} className="group relative">
                  <div
                    className={`relative flex h-24 items-center justify-center overflow-hidden rounded-md border bg-gray-100 ${
                      index === primaryImageIndex ? "ring-2 ring-blue-500" : ""
                    }`}
                  >
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="h-full w-full object-cover"
                    />
                    {primaryImageIndex === index && (
                      <span className="absolute left-1 top-1 z-20 rounded bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        Primary
                      </span>
                    )}
                    {index !== primaryImageIndex && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetPrimaryNew(index);
                        }}
                        className="absolute bottom-1 left-1 z-20 rounded-full bg-white p-1 text-gray-600 opacity-0 shadow-md group-hover:opacity-100"
                        title="Set as Primary Image"
                      >
                        ★
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProjectImage(index)}
                    className="absolute -right-2 -top-2 z-20 rounded-full bg-red-500 p-1 text-white opacity-0 shadow-md hover:bg-red-600 group-hover:opacity-100"
                  >
                    ×
                  </button>
                  {index !== primaryImageIndex ? (
                    <div className="mt-1">
                      <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                        Position
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[1-9][0-9]*"
                        value={newImagePositions[index] || ""}
                        onChange={(e) =>
                          onNewImagePositionChange(index, e.target.value)
                        }
                        className="ui-input-field w-full px-1 py-1 text-xs"
                        placeholder="1"
                      />
                    </div>
                  ) : (
                    <p className="mt-1 text-center text-[10px] font-medium text-blue-600">
                      Primary (not indexed)
                    </p>
                  )}
                  <p className="mt-0.5 truncate text-center text-[10px] text-gray-500" title={image.name}>
                    {image.name}
                  </p>
                  <ImageSeoFields
                    stacked
                    imageAlt={image.imageAlt || ""}
                    imageTitle={image.imageTitle || ""}
                    onAltChange={(value) =>
                      onNewProjectImageSeoChange(index, "image_alt", value)
                    }
                    onTitleChange={(value) =>
                      onNewProjectImageSeoChange(index, "image_title", value)
                    }
                    className="mt-2"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
